import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';

const AffordableCarsManager = () => {
  const [viewMode, setViewMode] = useState('add'); // 'add' or 'view'
  const [allCars, setAllCars] = useState([]);
  const [affordableCars, setAffordableCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [deleteDialog, setDeleteDialog] = useState({ show: false, car: null, isAll: false, progress: null });
  const [isCancelled, setIsCancelled] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const SpinningLogo = styled("img")(({ theme }) => ({
    width: "150px",
    height: "150px",
  }));

  const fetchAllCars = async (page = 0) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/master/affordable-car/get-available-models?page=${page}&size=100`,
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const result = await response.json();

      if (result.success) {
        setAllCars(result.data.carModels || []);
        setTotalPages(result.data.totalPages || 0);
        setTotalRecords(result.data.totalRecords || 0);
        setHasNext(result.data.hasNext || false);
        setHasPrevious(result.data.hasPrevious || false);
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
      showToast('Failed to fetch cars', 'error');
    } finally {
      setLoading(false);
    }
  };

  const searchAllCars = async (searchQuery) => {
    if (!searchQuery.trim()) {
      fetchAllCars(currentPage);
      return;
    }

    try {
      setIsSearching(true);
      setLoading(true);

      const allPages = [];
      const totalPagesToFetch = Math.ceil(totalRecords / 100);

      for (let page = 0; page < totalPagesToFetch; page++) {
        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-model/get-available-models?page=${page}&size=100`,
          {
            headers: {
              'accept': 'application/json',
              'Accept-Language': 'en'
            }
          }
        );
        const result = await response.json();

        if (result.success && result.data.carModels) {
          allPages.push(...result.data.carModels);
        }
      }

      const searchLower = searchQuery.toLowerCase();
      const filtered = allPages.filter(car =>
        car.carModelName?.toLowerCase().includes(searchLower) ||
        car.carBrandName?.toLowerCase().includes(searchLower)
      );

      setAllCars(filtered);
      setTotalPages(1);
      setHasNext(false);
      setHasPrevious(false);

    } catch (error) {
      console.error('Error searching cars:', error);
      showToast('Failed to search cars', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAffordableCars = async () => {
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/master/affordable-car/get-all-affordable-car?page=0&size=500',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const result = await response.json();
      setAffordableCars(result.data || []);
    } catch (error) {
      console.error('Error fetching affordable cars:', error);
    }
  };

  useEffect(() => {
    if (viewMode === 'add' && !isSearching) {
      fetchAllCars(currentPage);
    }
    fetchAffordableCars();
  }, [currentPage, viewMode]);

  useEffect(() => {
    if (viewMode === 'add') {
      const timer = setTimeout(() => {
        if (searchTerm) {
          searchAllCars(searchTerm);
        } else {
          setIsSearching(false);
          fetchAllCars(currentPage);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [searchTerm, viewMode]);

  const addToAffordable = async (carModelId) => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/master/affordable-car/add-to-affordable?carModelId=${carModelId}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json'
          },
          body: ''
        }
      );

      const result = await response.json();

      if (response.ok) {
        showToast('Added to Affordable section successfully', 'success');

        // Update affordable cars list
        await fetchAffordableCars();

        // Remove the added car from the current view without refetching
        setAllCars(prevCars => prevCars.filter(car => car.carModelId !== carModelId));
      } else {
        showToast(result.message || 'Failed to add car', 'error');
      }
    } catch (error) {
      console.error('Error adding to Affordable:', error);
      showToast('Error adding car to Affordable section', 'error');
    }
  };

  const showDeleteDialog = (car, isAll = false) => {
    setIsCancelled(false);
    setDeleteDialog({ show: true, car, isAll, progress: null });
  };

  const hideDeleteDialog = () => {
    setIsCancelled(true);
    setDeleteDialog({ show: false, car: null, isAll: false, progress: null });
  };

  const removeFromAffordable = async (affordableCarId) => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/master/affordable-car/remove-from-affordable?affordableCarId=${affordableCarId}`,
        {
          method: 'DELETE',
          headers: {
            'accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        showToast('Removed from Affordable section successfully', 'success');
        setAffordableCars(prev => prev.filter(car => car.affordableCarId !== affordableCarId));
      } else {
        const result = await response.json();
        showToast(result.message || 'Failed to remove car', 'error');
      }
    } catch (error) {
      console.error('Error removing from Affordable:', error);
      showToast('Error removing car from affordable section', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.isAll) {
      await removeAllFromAffordable();
    } else if (deleteDialog.car) {
      await removeFromAffordable(deleteDialog.car.affordableCarId);
      hideDeleteDialog();
    }
  };

  const removeAllFromAffordable = async () => {
    try {
      setLoading(true);
      setIsCancelled(false);
      let successCount = 0;
      let errorCount = 0;
      const totalCars = affordableCars.length;

      for (let i = 0; i < affordableCars.length; i++) {
        if (isCancelled) {
          showToast(`Operation cancelled. Removed ${successCount} of ${totalCars} cars`, 'warning');
          setLoading(false);
          hideDeleteDialog();
          return;
        }

        const car = affordableCars[i];
        try {
          const response = await fetch(
            `https://car.amar.co.jp/api/master/affordable-car/remove-from-affordable?affordableCarId=${car.affordableCarId}`,
            {
              method: 'DELETE',
              headers: {
                'accept': 'application/json'
              }
            }
          );

          if (response.ok) {
            successCount++;
            setAffordableCars(prev => prev.filter(c => c.affordableCarId !== car.affordableCarId));
          } else {
            errorCount++;
          }
        } catch (err) {
          errorCount++;
        }

        setDeleteDialog(prev => ({
          ...prev,
          progress: {
            current: i + 1,
            total: totalCars,
            percentage: Math.round(((i + 1) / totalCars) * 100)
          }
        }));
      }

      if (errorCount === 0) {
        showToast(`All ${successCount} cars removed successfully`, 'success');
      } else {
        showToast(`Removed ${successCount} cars, ${errorCount} failed`, 'warning');
      }
    } catch (error) {
      showToast('Error removing cars', 'error');
    } finally {
      setLoading(false);
      hideDeleteDialog();
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2; // how many pages to show around current page
    const range = [];

    for (
      let i = Math.max(0, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (range[0] > 0) {
      pages.push(0);
      if (range[0] > 1) pages.push('...');
    }

    pages.push(...range);

    if (range[range.length - 1] < totalPages - 1) {
      if (range[range.length - 1] < totalPages - 2) pages.push('...');
      pages.push(totalPages - 1);
    }

    return pages;
  };

  const isAffordable = (carModelId) => {
    return affordableCars.some(car => car.carModelId === carModelId);
  };

  const availableCars = allCars.filter(car => !isAffordable(car.carModelId));

  const filteredAffordableCars = affordableCars.filter(car =>
    car.carModelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    car.carBrandName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    car.carCategoriesName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const styles = {
    container: {
      padding: '15px',
      maxWidth: '1400px',
      margin: '0 auto',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: '#f5f5f5',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '20px',
      paddingBottom: '15px',
      borderBottom: '2px solid #e0e0e0'
    },
    title: {
      fontSize: '24px',
      color: '#2c3e50',
      marginBottom: '5px',
      fontWeight: '600'
    },
    viewToggle: {
      display: 'flex',
      gap: '10px',
      marginTop: '12px',
      marginBottom: '12px'
    },
    toggleBtn: {
      padding: '10px 20px',
      border: '2px solid #eb2329',
      borderRadius: '6px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      background: 'white',
      color: '#eb2329'
    },
    toggleBtnActive: {
      background: '#eb2329',
      color: 'white',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.3)'
    },
    statsBar: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)',
      padding: '12px 20px',
      borderRadius: '8px',
      display: 'flex',
      gap: '25px',
      marginTop: '12px',
      flexWrap: 'wrap',
      boxShadow: '0 4px 15px rgba(235, 35, 41, 0.25)',
      border: '1px solid rgba(235, 35, 41, 0.2)',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    statsLeft: {
      display: 'flex',
      gap: '25px',
      flexWrap: 'wrap'
    },
    stat: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    statLabel: {
      fontSize: '11px',
      color: 'rgba(255,255,255,0.9)',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    statValue: {
      fontSize: '20px',
      color: 'white',
      fontWeight: 'bold'
    },
    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px',
      gap: '10px',
      flexWrap: 'wrap'
    },
    searchInput: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      width: '280px',
      fontSize: '13px',
      outline: 'none',
      transition: 'all 0.2s',
      background: 'white',
      color: '#2c3e50'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '12px',
      marginBottom: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      border: '1px solid #e0e0e0'
    },
    affordableCard: {
      boxShadow: '0 4px 15px rgba(235, 35, 41, 0.15)',
      border: '2px solid #eb2329'
    },
    imageContainer: {
      position: 'relative',
      height: '130px',
      overflow: 'hidden',
      background: '#f8f8f8'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    badge: {
      position: 'absolute',
      top: '10px',
      left: '10px',
      background: 'linear-gradient(135deg, #eb2329 0%, #ff4444 100%)',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    cardInfo: {
      padding: '10px'
    },
    cardTitle: {
      fontSize: '13px',
      color: '#2c3e50',
      marginBottom: '4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: '600'
    },
    cardBrand: {
      color: '#7f8c8d',
      fontSize: '11px',
      marginBottom: '6px'
    },
    cardPrice: {
      fontSize: '15px',
      fontWeight: '700',
      color: '#eb2329',
      marginBottom: '8px'
    },
    button: {
      padding: '7px 14px',
      border: 'none',
      borderRadius: '6px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '12px',
      width: '100%',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    addBtn: {
      background: '#eb2329',
      color: 'white',
      boxShadow: '0 2px 4px rgba(235, 35, 41, 0.3)'
    },
    removeBtn: {
      background: '#dc3545',
      color: 'white',
      boxShadow: '0 2px 4px rgba(220, 53, 69, 0.3)'
    },
    clearAllBtn: {
      background: '#8b0000',
      color: '#fff',
      padding: '8px 20px',
      boxShadow: '0 2px 4px rgba(139, 0, 0, 0.3)'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '15px',
      marginTop: '20px',
      padding: '15px',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid #e0e0e0'
    },
    paginationBtn: {
      padding: '8px 16px',
      background: '#eb2329',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '12px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.3)'
    },

    paginationBtn: {
      padding: '8px 16px',
      background: '#eb2329',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '12px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.3)'
    },
    paginationBtnDisabled: {
      background: '#ddd',
      cursor: 'not-allowed',
      boxShadow: 'none',
      color: '#999'
    },
    pageNumBtn: {
      padding: '8px 13px',
      background: 'white',
      color: '#120b0bff',
      border: '1px solid #120b0bff',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '12px',
      transition: 'all 0.2s ease',
      minWidth: '38px'
    },
    pageNumBtnActive: {
      background: '#eb2329',
      color: 'white',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.3)'
    },
    pageEllipsis: {
      padding: '8px 4px',
      color: '#7f8c8d',
      fontWeight: '600',
      fontSize: '12px'
    },
    paginationBtnDisabled: {
      background: '#ddd',
      cursor: 'not-allowed',
      boxShadow: 'none',
      color: '#999'
    },
    paginationInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px'
    },
    loading: {
      textAlign: 'center',
      padding: '30px',
      color: '#7f8c8d',
      fontSize: '14px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#95a5a6'
    },
    toast: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      fontSize: '13px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      zIndex: 10000,
      animation: 'slideIn 0.3s ease',
      minWidth: '250px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    toastSuccess: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)'
    },
    toastError: {
      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
    },
    toastWarning: {
      background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
    },
    searchBadge: {
      display: 'inline-block',
      background: '#eb2329',
      color: 'white',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      marginLeft: '8px'
    },
    dialogOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      animation: 'fadeIn 0.2s ease'
    },
    dialog: {
      background: 'white',
      borderRadius: '12px',
      maxWidth: '480px',
      width: '90%',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      overflow: 'hidden',
      animation: 'slideUp 0.3s ease'
    },
    dialogHeader: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)',
      padding: '20px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    dialogIcon: {
      width: '48px',
      height: '48px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      color: 'white'
    },
    dialogHeaderText: {
      flex: 1
    },
    dialogTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: 'white',
      marginBottom: '4px'
    },
    dialogSubtitle: {
      fontSize: '13px',
      color: 'rgba(255, 255, 255, 0.9)'
    },
    dialogBody: {
      padding: '24px'
    },
    dialogMessage: {
      fontSize: '15px',
      color: '#2c3e50',
      lineHeight: '1.6',
      marginBottom: '20px'
    },
    carInfoBox: {
      background: '#f8f9fa',
      border: '2px solid #eb2329',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px'
    },
    carInfoTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '8px'
    },
    carInfoDetails: {
      fontSize: '13px',
      color: '#7f8c8d',
      lineHeight: '1.5'
    },
    progressContainer: {
      marginTop: '16px',
      marginBottom: '16px'
    },
    progressBar: {
      width: '100%',
      height: '8px',
      background: '#e9ecef',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '8px'
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(135deg, #eb2329 0%, #ff4444 100%)',
      transition: 'width 0.3s ease',
      borderRadius: '4px'
    },
    progressText: {
      fontSize: '13px',
      color: '#6c757d',
      textAlign: 'center',
      fontWeight: '600'
    },
    dialogActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    dialogButton: {
      padding: '10px 24px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    dialogCancelButton: {
      background: 'white',
      color: '#6c757d',
      border: '2px solid #dee2e6'
    },
    dialogConfirmButton: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(235, 35, 41, 0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    processingSpinner: {
      display: 'inline-block',
      width: '14px',
      height: '14px',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      borderTopColor: 'white',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {toast.show && (
        <div style={{
          ...styles.toast,
          ...(toast.type === 'success' ? styles.toastSuccess :
            toast.type === 'error' ? styles.toastError :
              styles.toastWarning)
        }}>
          <span style={{ fontSize: '18px' }}>
            {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : '⚠'}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {deleteDialog.show && (
        <div style={styles.dialogOverlay} onClick={() => !deleteDialog.progress && hideDeleteDialog()}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogHeader}>
              <div style={styles.dialogIcon}>⚠</div>
              <div style={styles.dialogHeaderText}>
                <div style={styles.dialogTitle}>Confirm Removal</div>
                <div style={styles.dialogSubtitle}>
                  {deleteDialog.isAll ? 'Remove all affordable cars' : 'Remove from affordable section'}
                </div>
              </div>
            </div>

            <div style={styles.dialogBody}>
              <p style={styles.dialogMessage}>
                {deleteDialog.isAll
                  ? `Are you sure you want to remove all ${affordableCars.length} cars from the affordable section? This action cannot be undone.`
                  : 'Are you sure you want to remove this car from the affordable section? This action cannot be undone.'}
              </p>

              {!deleteDialog.isAll && deleteDialog.car && (
                <div style={styles.carInfoBox}>
                  <div style={styles.carInfoTitle}>{deleteDialog.car.carModelName}</div>
                  <div style={styles.carInfoDetails}>
                    <div>{deleteDialog.car.carBrandName}</div>
                    <div style={{ color: '#eb2329', fontWeight: '600', marginTop: '4px' }}>
                      ${deleteDialog.car.carPrice?.toLocaleString()}
                    </div>
                  </div>
                </div>
              )}

              {deleteDialog.isAll && deleteDialog.progress && (
                <div style={styles.progressContainer}>
                  <div style={styles.progressBar}>
                    <div style={{
                      ...styles.progressFill,
                      width: `${deleteDialog.progress.percentage}%`
                    }} />
                  </div>
                  <div style={styles.progressText}>
                    Processing: {deleteDialog.progress.current} / {deleteDialog.progress.total} cars ({deleteDialog.progress.percentage}%)
                  </div>
                </div>
              )}

              <div style={styles.dialogActions}>
                <button
                  style={{ ...styles.dialogButton, ...styles.dialogCancelButton }}
                  onClick={hideDeleteDialog}
                >
                  Cancel
                </button>
                <button
                  style={{ ...styles.dialogButton, ...styles.dialogConfirmButton }}
                  onClick={handleDeleteConfirm}
                  disabled={deleteDialog.progress}
                >
                  {deleteDialog.progress ? (
                    <>
                      <span style={styles.processingSpinner}></span>
                      Processing...
                    </>
                  ) : (
                    'Yes, Remove'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.header}>
        <h2 style={styles.title}>Manage Affordable Cars</h2>

        <div style={styles.viewToggle}>
          <button
            style={{
              ...styles.toggleBtn,
              ...(viewMode === 'add' && styles.toggleBtnActive)
            }}
            onClick={() => {
              setViewMode('add');
              setSearchTerm('');
            }}
          >
            Add Affordable Cars
          </button>
          <button
            style={{
              ...styles.toggleBtn,
              ...(viewMode === 'view' && styles.toggleBtnActive)
            }}
            onClick={() => {
              setViewMode('view');
              setSearchTerm('');
            }}
          >
            View Affordable Cars ({affordableCars.length})
          </button>
        </div>

        {viewMode === 'add' ? (
          <div style={styles.statsBar}>
            <div style={styles.stat}>
              <span style={styles.statLabel}>Total Available</span>
              <span style={styles.statValue}>{totalRecords.toLocaleString()}</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statLabel}>Already Affordable</span>
              <span style={styles.statValue}>{affordableCars.length}</span>
            </div>
            {!isSearching && (
              <div style={styles.stat}>
                <span style={styles.statLabel}>Page</span>
                <span style={styles.statValue}>{currentPage + 1}/{totalPages}</span>
              </div>
            )}
          </div>
        ) : (
          <div style={styles.statsBar}>
            <div style={styles.statsLeft}>
              <div style={styles.stat}>
                <span style={styles.statLabel}>Total Affordable Cars</span>
                <span style={styles.statValue}>{affordableCars.length}</span>
              </div>
              <div style={styles.stat}>
                <span style={styles.statLabel}>Filtered Results</span>
                <span style={styles.statValue}>{filteredAffordableCars.length}</span>
              </div>
            </div>
            {affordableCars.length > 0 && (
              <button
                style={{ ...styles.button, ...styles.clearAllBtn }}
                onClick={() => showDeleteDialog(null, true)}
                disabled={loading}
              >
                {loading ? 'Removing...' : 'Clear All Affordable'}
              </button>
            )}
          </div>
        )}
      </div>

      <div>
        <div style={styles.topBar}>
          <h3 style={{ fontSize: '16px', color: '#2c3e50', fontWeight: '600', margin: 0 }}>
            {viewMode === 'add' ? 'Available Cars (Not in Affordable)' : 'Affordable Cars'}
            {viewMode === 'add' && isSearching && searchTerm && (
              <span style={styles.searchBadge}>
                Searching: {availableCars.length} results
              </span>
            )}
          </h3>
          <input
            type="text"
            placeholder={viewMode === 'add' ? "Search entire inventory..." : "Search Affordable cars..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {loading && (viewMode === 'add' || affordableCars.length === 0) ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
              flexDirection: "column",
              gap: 2
            }}
          >
            <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
            <Typography variant="body1">Loading car data...</Typography>
          </Box>
        ) : (
          <>
            {viewMode === 'add' ? (
              <>
                {availableCars.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={{ fontSize: '14px', marginBottom: '5px' }}>
                      {searchTerm ? 'No cars match your search' : 'No available cars to add'}
                    </p>
                    <p style={{ fontSize: '12px' }}>
                      {searchTerm ? 'Try a different search term' : 'All cars are already in Affordable section'}
                    </p>
                  </div>
                ) : (
                  <div style={styles.grid}>
                    {availableCars.map((car) => (
                      <div key={car.carModelId} style={styles.card}>
                        <div style={styles.imageContainer}>
                          <img
                            src={`https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${car.carModelId}`}
                            alt={car.carModelName}
                            onError={(e) => e.target.src = '/carousel/carslider.png'}
                            style={styles.image}
                          />
                        </div>
                        <div style={styles.cardInfo}>
                          <h4 style={styles.cardTitle}>{car.carModelName}</h4>
                          <p style={styles.cardBrand}>{car.carBrandName}</p>
                          <p style={styles.cardPrice}>${car.carPrice?.toLocaleString()}</p>
                          <button
                            style={{ ...styles.button, ...styles.addBtn }}
                            onClick={() => addToAffordable(car.carModelId)}
                          >
                            Add to Affordable
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!isSearching && (
                  <div style={styles.pagination}>
                    <button
                      style={{


                        ...styles.paginationBtn,
                        ...(!hasPrevious && styles.paginationBtnDisabled)
                      }}
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={!hasPrevious}
                    >
                      Previous
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {getPageNumbers().map((page, idx) =>
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} style={styles.pageEllipsis}>...</span>
                        ) : (
                          <button
                            key={page}
                            style={{
                              ...styles.pageNumBtn,
                              ...(page === currentPage && styles.pageNumBtnActive)
                            }}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page + 1}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      style={{
                        ...styles.paginationBtn,
                        ...(!hasNext && styles.paginationBtnDisabled)
                      }}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!hasNext}
                    >
                      Next
                    </button>
                  </div>
                )}


              </>
            ) : (
              <>
                {filteredAffordableCars.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3 }}>🚗</div>
                    <p style={{ fontSize: '18px', marginBottom: '10px', color: '#7f8c8d' }}>
                      {searchTerm ? 'No cars match your search' : 'No Affordable cars added yet'}
                    </p>
                    <p style={{ fontSize: '14px', color: '#95a5a6' }}>
                      {searchTerm ? 'Try a different search term' : 'Switch to "Add Affordable Cars" to add cars'}
                    </p>
                  </div>
                ) : (
                  <div style={styles.grid}>
                    {filteredAffordableCars.map((car, index) => (
                      <div key={car.carModelId} style={{ ...styles.card, ...styles.affordableCard }}>
                        <div style={styles.imageContainer}>
                          <img
                            src={`https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${car.carModelId}`}
                            alt={car.carModelName}
                            onError={(e) => e.target.src = '/carousel/carslider.png'}
                            style={styles.image}
                          />
                          <span style={styles.badge}>#{index + 1}</span>
                        </div>
                        <div style={styles.cardInfo}>
                          <h4 style={styles.cardTitle}>{car.carModelName}</h4>
                          <p style={styles.cardBrand}>{car.carBrandName || car.carCategoriesName}</p>
                          <p style={styles.cardPrice}>${car.carPrice?.toLocaleString()}</p>
                          <button
                            style={{ ...styles.button, ...styles.removeBtn }}
                            onClick={() => showDeleteDialog(car, false)}
                          >
                            Remove from Affordable
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AffordableCarsManager;